import { apiClient } from "@/lib/apiClient"
import { formatDateLong } from "@/utils"
import type { CatalogCardProps } from "@/types/component"
import { getDeliveryTypeLabel } from "../utils"
import type { EducationDetailApiResponse } from "../types/education-detail.api.types"
import type { VirtualClassroomParticipant } from "../types/virtual-classroom.types"
import { formatDurationFloat } from "@/utils/format-duration"

export {
  EnrollmentStatus,
  BackendEducationType,
  type BackendEducationTypeValue,
  type EducationDetailApiResponse,
  type BackendEducationDetail,
  type BackendChapter,
  type BackendObjective,
} from "../types/education-detail.api.types"

export type { VirtualClassroomParticipant } from "../types/virtual-classroom.types"
export { formatVirtualClassroomParticipantName } from "../types/virtual-classroom.types"

const CLIENT_CATALOG_ENDPOINT = "/api/client/educations/catalog"
const CLIENT_EDUCATION_ENDPOINT = "/api/client/educations"
const CLIENT_EDUCATION_CATEGORIES_ENDPOINT = "/api/client/education-categories"
/** Sanal sınıf katılımcıları — backend path Swagger ile eşleştirin */
const CLIENT_CLASSROOMS_ENDPOINT = "/api/client/classrooms"
const USERS_INSTRUCTORS_ENDPOINT = "/api/users/instructors"
const CLIENT_FILES_STREAM_ENDPOINT = "/api/client/files"
const CLIENT_OBJECTIVES_VIDEO_WATCH_ENDPOINT = "/api/client/objectives"
const CLIENT_EDUCATION_ENROLLMENTS_ENDPOINT = "/api/client/education-enrollments"

/** Backend type: 0=ON_SITE, 1=ONLINE, 2=UNIT, 3=NONUNIT */
export const EDUCATION_TYPE = {
  ON_SITE: 0,
  ONLINE: 1,
  UNIT: 2,
  NONUNIT: 3,
} as const

/** Backend completionStatus: 0=NOT_STARTED, 1=IN_PROGRESS, 2=COMPLETED */
export const COMPLETION_STATUS = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
} as const

const COMPLETION_STATUS_MAP: Record<number, "not_started" | "in_progress" | "completed"> = {
  [COMPLETION_STATUS.NOT_STARTED]: "not_started",
  [COMPLETION_STATUS.IN_PROGRESS]: "in_progress",
  [COMPLETION_STATUS.COMPLETED]: "completed",
}

/** Backend objective type: 0=INTERNAL_FILE, 1=LINK, 2=JITSI */
export const OBJECTIVE_TYPE_JITSI = 2

/** GET /api/client/educations/:id - Eğitim detay response tipi (backend: education-chapters-objectives) */
export interface EducationLesson {
  id: string
  title?: string
  name?: string
  durationMinutes?: number
  duration?: number | string
  /** Backend: 0=INTERNAL_FILE, 1=LINK, 2=JITSI */
  type?: number
  /** Backend: completionStatus 0=NOT_STARTED, 1=IN_PROGRESS, 2=COMPLETED */
  status?: "completed" | "in_progress" | "not_started"
  completionStatus?: number
  order?: number
  fileID?: string
  externalUrl?: string | null
  file?: { id?: string; durationMinutes?: number; [key: string]: any }
  [key: string]: any
}

export interface EducationUnit {
  id: string
  title?: string
  name?: string
  order?: number
  durationMinutes?: number
  duration?: number | string
  completedLessons?: number
  totalLessons?: number
  progress?: number
  progressPercentage?: number | null
  /** Backend: lessons | objectives | items | contents */
  lessons?: EducationLesson[]
  objectives?: EducationLesson[]
  items?: EducationLesson[]
  contents?: EducationLesson[]
  [key: string]: any
}

export interface ClientEducationDetail {
  id: string
  name?: string
  title?: string
  educationName?: string
  description?: string
  /** Backend: categories dizisi */
  categories?: Array<{ id?: string; name?: string; label?: string; [key: string]: any }>
  /** Backend: instructors dizisi */
  instructors?: Array<{ id?: string; firstName?: string; lastName?: string; name?: string; [key: string]: any }>
  instructor?: string
  instructorName?: string
  educatorName?: string
  /** Backend: type number 0=ON_SITE, 1=ONLINE, 2=UNIT, 3=NONUNIT */
  type?: number
  format?: string
  deliveryType?: string
  category?: string
  categoryName?: string
  coverImageFileID?: string | null
  /** Backend'den gelen hazır kapak URL'i (download yerine kullan) */
  coverImageUrl?: string | null
  coverImage?: string
  image?: string
  videoUrl?: string
  videoEmbedUrl?: string
  /** Backend: durationHours + durationMinutes */
  durationHours?: number
  durationMinutes?: number
  duration?: number | string
  startDate?: string
  endDate?: string
  completionDate?: string
  requestCollectionStartDate?: string
  requestCollectionEndDate?: string
  createdAt?: string
  updatedAt?: string
  capacity?: number
  budget?: number
  scheduledTimes?: Array<{ day?: number; startTime?: string; endTime?: string }>
  /** Backend: chapters (education-chapter-objective hiyerarşisi) */
  chapters?: EducationUnit[]
  units?: EducationUnit[]
  sections?: EducationUnit[]
  curriculum?: EducationUnit[]
  progress?: {
    hoursRemaining?: number
    completedLessons?: number
    totalLessons?: number
    percentage?: number
    [key: string]: any
  }
  /** Backend: Kullanıcının bu eğitime kayıt durumu (null = talep yok) */
  enrollment?: { status?: number | null; progressPercent?: number | null } | null
  [key: string]: any
}

/** Backend catalog/detail response - eğitim listesi ve detay için ortak tip */
export interface ClientEducationCatalogItem {
  id: string
  name?: string
  title?: string
  educationName?: string
  description?: string
  categories?: Array<{ id?: string; name?: string; label?: string; [key: string]: any }>
  instructors?: Array<{ id?: string; firstName?: string; lastName?: string; name?: string; [key: string]: any }>
  instructor?: string
  instructorName?: string
  educatorName?: string
  coverImageFileID?: string | null
  coverImageUrl?: string | null
  coverImage?: string
  image?: string
  /** Backend: 0=ON_SITE, 1=ONLINE, 2=UNIT, 3=NONUNIT */
  type?: number
  format?: string
  deliveryType?: string
  category?: string
  categoryName?: string
  durationHours?: number
  durationMinutes?: number
  duration?: number
  capacity?: number
  registrationCount?: number
  maxRegistration?: number
  maxParticipants?: number
  requestCollectionEndDate?: string
  completionDate?: string
  startDate?: string
  endDate?: string
  status?: string
  /** Zorunlu eğitim bayrağı — admin tarafında işaretlendiğinde kartta "Zorunlu" chip'i çıkar */
  isRequired?: boolean
  /** Backend: Kullanıcının bu eğitime kayıt durumu (null = talep yok; API bazen string döner) */
  enrollment?: { status?: number | string | null } | null
  /** Backend: Eğitime bağlı sınav (varsa id ile) */
  exam?: { id?: string } | null
  /** Backend: Eğitime bağlı anket (null değilse anket var) */
  survey?: unknown | null
  [key: string]: any
}

export interface ClientCatalogResponse {
  data?: ClientEducationCatalogItem[]
  items?: ClientEducationCatalogItem[]
  count?: number
  total?: number
  page?: number
  pageSize?: number
}

const DEFAULT_CARD_IMAGE = "https://picsum.photos/seed/edu/400/200"

/** Backend enrollment status: 2 = ACTIVE (Onaylandı) → detaya gidilebilir */
export const ENROLLMENT_STATUS_ACTIVE = 2;

/** Katalog kartı buton aksiyonu: detay, talep, sınav veya anket */
export type CatalogButtonAction = "detail" | "request" | "exam" | "survey"

/** Katalog API'den dönen kart tipi (buttonLabelKey çeviri anahtarı) */
export type CatalogCardItemWithKey = Omit<
  CatalogCardProps,
  "buttonLabel"
> & {
  id: string
  buttonLabelKey: string
  /** Süre formatı sayfada locale ile kullanılır (s/dk veya h/m) */
  durationHours?: number
  durationMinutes?: number
  enrollmentStatus?: number | null
  /** Buton tıklanınca yapılacak aksiyon (exam = sınav sayfası, survey = anket sayfası) */
  buttonAction?: CatalogButtonAction
}

/** enrollment.status'a göre çeviri anahtarını döndürür (t() ile kullanın) */
export function getEnrollmentStatusKey(status: number | null | undefined): string {
  if (status == null) return "educationCatalog.enrollment.status.requestCreate"
  switch (status) {
    case 0:
      return "educationCatalog.enrollment.status.sent"
    case 1:
      return "educationCatalog.enrollment.status.pending"
    case 2:
      return "educationCatalog.enrollment.status.approved"
    case 3:
    case 4:
      return "educationCatalog.enrollment.status.rejected"
    case 5:
      return "educationCatalog.enrollment.status.cancelled"
    case 6:
      return "educationCatalog.enrollment.status.contentCompleted"
    case 7:
      return "educationCatalog.enrollment.status.examFailed"
    case 8:
      return "educationCatalog.enrollment.status.examCompleted"
    case 9:
      return "educationCatalog.enrollment.status.completed"
    case 11:
      return "clientEducations.status.notCompleted"
    case 65:
      return "educationCatalog.enrollment.status.examStarted"
    case 82:
      return "educationCatalog.enrollment.status.surveyStarted"
    default:
      return "educationCatalog.enrollment.status.details"
  }
}

/**
 * Ham katalog öğesinden exam/survey/enrollment'ı okur.
 * Backend bazen education/data içinde sarıyor; bazen doğrudan item'da.
 */
function getCatalogItemForButton(
  item: ClientEducationCatalogItem & {
    education?: Record<string, unknown>
    data?: Record<string, unknown>
    [key: string]: unknown
  }
): ClientEducationCatalogItem {
  const root = item
  const nested = (root.education ?? root.data) as
    | { exam?: unknown; survey?: unknown; enrollment?: unknown }
    | undefined
  const exam = root.exam ?? nested?.exam
  const survey = root.survey ?? nested?.survey
  const enrollment = root.enrollment ?? nested?.enrollment
  if (exam === undefined && survey === undefined && enrollment === undefined && !nested)
    return root
  return {
    ...root,
    exam: exam ?? root.exam,
    survey: survey ?? root.survey,
    enrollment: enrollment ?? root.enrollment,
  } as ClientEducationCatalogItem
}

/** Backend exam: { id, passingScore? } veya bazen sadece id string; survey: uuid string veya object */
function hasExamId(item: ClientEducationCatalogItem): boolean {
  const exam = item.exam as { id?: string } | string | null | undefined
  if (exam == null) return false
  if (typeof exam === "string") return String(exam).trim() !== ""
  const id = exam.id
  return typeof id === "string" && id.trim() !== ""
}

function hasSurveyDefined(item: ClientEducationCatalogItem): boolean {
  const s = item.survey
  if (s == null) return false
  if (typeof s === "string") return s.trim() !== ""
  return true
}

/**
 * enrollment status + exam/survey varlığına göre buton metni ve aksiyonu.
 * Eğitim kataloğu ve Eğitimlerim sayfalarında ortak kullanım için export edilir.
 */
export function getEnrollmentButtonConfig(
  status: number | string | null | undefined,
  hasExam: boolean,
  hasSurvey: boolean
): { buttonLabelKey: string; buttonDisabled: boolean; buttonAction: CatalogButtonAction } {
  const raw = status != null && status !== "" ? Number(status) : null
  const statusNum = typeof raw === "number" && !Number.isNaN(raw) ? raw : null

  if (statusNum === 9) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.completed",
      buttonDisabled: true,
      buttonAction: "detail",
    }
  }
  // NOT_COMPLETED (11): yönetici "tamamlamadı" kararı verdi → ilerleme yok
  if (statusNum === 11) {
    return {
      buttonLabelKey: "clientEducations.status.notCompleted",
      buttonDisabled: true,
      buttonAction: "detail",
    }
  }
  // CONTENT_COMPLETED (6): exam varsa Sınav'a başla, yoksa survey varsa Anket'e başla, yoksa Sınav'a başla (take sayfası exam yoksa halleder)
  if (statusNum === 6) {
    if (hasSurvey && !hasExam) {
      return {
        buttonLabelKey: "educationCatalog.enrollment.button.startSurvey",
        buttonDisabled: false,
        buttonAction: "survey",
      }
    }
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.startExam",
      buttonDisabled: false,
      buttonAction: "exam",
    }
  }
  // EXAM_STARTED (65) => Sınav'a devam et
  if (statusNum === 65) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.continueExam",
      buttonDisabled: false,
      buttonAction: "exam",
    }
  }
  // EXAM_FAILED (7) => Sınav'a tekrar dene
  if (statusNum === 7) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.retryExam",
      buttonDisabled: false,
      buttonAction: "exam",
    }
  }
  // EXAM_COMPLETED (8) + survey => Anket'e başla
  if (statusNum === 8 && hasSurvey) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.startSurvey",
      buttonDisabled: false,
      buttonAction: "survey",
    }
  }
  // SURVEY_STARTED (82) + survey => Anket'e başla
  if (statusNum === 82 && hasSurvey) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.button.startSurvey",
      buttonDisabled: false,
      buttonAction: "survey",
    }
  }
  // ENROLLED (2) veya yukarıdakilerden biri değilse → detay veya talep
  if (statusNum === 2) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.status.details",
      buttonDisabled: false,
      buttonAction: "detail",
    }
  }
  if (
    statusNum != null &&
    statusNum !== 0 &&
    statusNum !== 1 &&
    statusNum !== 3 &&
    statusNum !== 4 &&
    statusNum !== 5
  ) {
    // Diğer aktif sayılabilecek durumlar → detay
    return {
      buttonLabelKey: "educationCatalog.enrollment.status.details",
      buttonDisabled: false,
      buttonAction: "detail",
    }
  }
  // Talep yok → Detayını Gör butonu (detay sayfasında "Talep Et" butonu var)
  if (statusNum == null) {
    return {
      buttonLabelKey: "educationCatalog.enrollment.status.requestCreate",
      buttonDisabled: false,
      buttonAction: "detail",
    }
  }
  // Talep bekliyor/reddedildi → sadece durum göster, buton disabled
  return {
    buttonLabelKey: getEnrollmentStatusKey(statusNum),
    buttonDisabled: true,
    buttonAction: "request",
  }
}

/** Katalog kartı buton metni ve aksiyonu (enrollment + exam + survey) */
function getCatalogButtonConfig(item: ClientEducationCatalogItem): {
  buttonLabelKey: string
  buttonDisabled: boolean
  buttonAction: CatalogButtonAction
} {
  const rawStatus = item.enrollment?.status
  const statusNum =
    rawStatus != null && rawStatus !== ""
      ? (typeof rawStatus === "number" ? rawStatus : Number(rawStatus))
      : null
  const num = typeof statusNum === "number" && !Number.isNaN(statusNum) ? statusNum : null
  return getEnrollmentButtonConfig(num, hasExamId(item), hasSurveyDefined(item))
}

function formatDuration(minutes?: number, durationStr?: string): string {
  if (durationStr) return String(durationStr)
  return formatDurationFloat(minutes)
}

function getCoverImageProps(item: ClientEducationCatalogItem): {
  imageFileId: string | null
  fallbackImageUrl: string | null
} {
  const directUrl = item.coverImageUrl ?? item.coverImage ?? item.image ?? null
  return {
    imageFileId: directUrl ? null : (item.coverImageFileID ?? null),
    fallbackImageUrl: directUrl || DEFAULT_CARD_IMAGE,
  }
}

function mapToCatalogCard(
  item: ClientEducationCatalogItem
): CatalogCardItemWithKey {
  const title = item.name ?? item.title ?? item.educationName ?? "-"

  const educatorName = (() => {
    const instr = item.instructors
    if (instr?.length) {
      const first = instr[0]
      const full = [first.firstName, first.lastName].filter(Boolean).join(" ")
      return full || (first.name ?? "-")
    }
    return (
      item.instructorName ??
      item.instructor ??
      item.educatorName ??
      item.educator ??
      "-"
    )
  })()

  const category = (() => {
    const cats = item.categories
    if (cats?.length) return cats[0]?.name ?? cats[0]?.label ?? "-"
    return item.category ?? item.categoryName ?? "-"
  })()

  const deliveryTypeLabel = getDeliveryTypeLabel(item.type, item.format)
  const deliveryType =
    deliveryTypeLabel !== "-"
      ? deliveryTypeLabel
      : item.deliveryType ?? item.format ?? "-"

  const rawItem = item as Record<string, unknown>
  const durationHours =
    item.durationHours ??
    (typeof rawItem.duration_hours === "number" ? rawItem.duration_hours : 0)
  const durationMinutes =
    item.durationMinutes ??
    (typeof rawItem.duration_minutes === "number" ? rawItem.duration_minutes : 0)
  const totalMinutes = durationHours * 60 + durationMinutes
  const duration =
    totalMinutes > 0
      ? formatDuration(totalMinutes)
      : formatDuration(
          item.durationMinutes ?? item.duration,
          typeof item.duration === "string" ? item.duration : undefined
        )

  const { imageFileId, fallbackImageUrl } = getCoverImageProps(item)

  const itemForButton = getCatalogItemForButton(
    item as ClientEducationCatalogItem & { education?: Record<string, unknown> }
  )
  const catalogButtonConfig = getCatalogButtonConfig(itemForButton)
  let buttonLabelKey = catalogButtonConfig.buttonLabelKey
  let btnDisabled = catalogButtonConfig.buttonDisabled
  const { buttonAction } = catalogButtonConfig

  const isStartDateFuture = (() => {
    if (!item.startDate) return false
    const ts = new Date(item.startDate).getTime()
    return Number.isFinite(ts) && ts > Date.now()
  })()

  // Buton artık startDate gelecekte olsa bile aktif kalıyor. Yayında
  // (status=ACTIVE) bir eğitimin detayına gitmek engellenmemeli — içerik/
  // sınav/anket zaten backend'de EDUCATION_NOT_YET_STARTED ile korunuyor
  // ve detay sayfasında "Eğitim X tarihinde başlayacak" bilgi kartı
  // kullanıcıyı yönlendiriyor. `isStartDateFuture` hâlâ aşağıda status
  // text'inde kullanıcıya bilgi vermek için tutuluyor.

  let statusType: "simple" | "deadline" | "participant" = "simple"
  let statusText: string | undefined
  let deadline: string | undefined
  let participantCount: number | undefined
  let maxParticipants: number | undefined
  let dateTime: string | undefined

  const hasCapacity = item.capacity != null || item.maxRegistration != null || item.maxParticipants != null
  if (hasCapacity) {
    statusType = "participant"
    participantCount =
      item.requestedEnrollmentsCount ??
      0
    maxParticipants = item.capacity ?? item.maxRegistration ?? item.maxParticipants ?? 0
    dateTime = item.endDate ?? item.requestCollectionEndDate
  } else if (item.requestCollectionEndDate ?? item.endDate) {
    statusType = "deadline"
    deadline = formatDateLong(item.requestCollectionEndDate ?? item.endDate)
  } else {
    statusText =
      item.registrationCount != null
        ? `${item.registrationCount} Katılımcı`
        : isStartDateFuture
          ? "Tarih Bekleniyor"
          : "Detaylar"
  }

  const buttonDisabled = btnDisabled

  return {
    id: item.id,
    imageFileId,
    fallbackImageUrl,
    title,
    description: item.description,
    educatorName,
    duration,
    durationHours,
    durationMinutes,
    deliveryType,
    category,
    statusType,
    statusText,
    deadline,
    participantCount,
    maxParticipants,
    dateTime: dateTime ? formatDateLong(dateTime) : undefined,
    buttonLabelKey,
    buttonDisabled,
    buttonAction,
    isRequired: item.isRequired === true,
    enrollmentStatus: (() => {
      const raw = item.enrollment?.status
      if (raw == null || raw === "") return undefined
      const n = typeof raw === "number" ? raw : Number(raw)
      return typeof n === "number" && !Number.isNaN(n) ? n : undefined
    })(),
  }
}

export interface EducationCategory {
  id: string
  name?: string
  label?: string
  parentID?: string | null
  subCategories?: EducationCategory[]
  [key: string]: any
}

export interface InstructorListItem {
  id: string
  firstName?: string
  lastName?: string
  [key: string]: any
}

/** Response'tan ünite listesini çıkarır (backend: chapters | units | sections) */
export function getUnitsFromDetail(detail: ClientEducationDetail | null): EducationUnit[] {
  if (!detail) return []
  const list = detail.chapters ?? detail.units ?? detail.sections ?? detail.curriculum
  return Array.isArray(list) ? list : []
}

/** Objective'ın video dosyası olup olmadığını kontrol eder (sadece video/mp4) */
export function isObjectiveVideo(lesson: EducationLesson): boolean {
  const mime = lesson.file?.mimeType
  return !!lesson.fileID && mime === "video/mp4"
}

export type ObjectiveContentType = "video" | "pdf" | "presentation" | "audio" | "document"

/** mimeType'a göre objective içerik tipini döndürür */
export function getObjectiveContentType(lesson: EducationLesson): ObjectiveContentType | null {
  if (!lesson.fileID) return null
  const mime = (lesson.file?.mimeType ?? "").toLowerCase()
  if (mime.startsWith("video/")) return "video"
  if (mime === "application/pdf") return "pdf"
  if (
    mime.includes("presentation") ||
    mime.includes("powerpoint") ||
    mime.includes("pptx") ||
    mime.includes("ppt")
  )
    return "presentation"
  if (mime.startsWith("audio/")) return "audio"
  return "document"
}

/** Objective tıklanabilir/gösterilebilir mi (fileID + tanınan tip) */
export function isObjectivePlayable(lesson: EducationLesson): boolean {
  return getObjectiveContentType(lesson) != null
}

/** Objective Jitsi mi (type === 2 ve externalUrl var) – iframe ile gösterilir */
export function isObjectiveJitsi(lesson: EducationLesson): boolean {
  return lesson.type === OBJECTIVE_TYPE_JITSI && !!lesson.externalUrl?.trim()
}

/** Ders seçilebilir mi: dosya/video veya Jitsi */
export function isObjectiveSelectable(lesson: EducationLesson): boolean {
  return isObjectivePlayable(lesson) || isObjectiveJitsi(lesson)
}

/** Objective video değilse (PDF, belge vb.) tıklanınca tamamlanacak – mimeType video/ ile başlamıyorsa true */
export function isObjectiveNonVideo(lesson: EducationLesson): boolean {
  const mime = (lesson.file?.mimeType ?? "").toLowerCase()
  return !!lesson.fileID && !mime.startsWith("video/")
}

/** Ünite içindeki ders listesini çıkarır (backend objectives/lessons/items/contents) */
export function getLessonsFromUnit(unit: EducationUnit): EducationLesson[] {
  const list = unit.objectives ?? unit.lessons ?? unit.items ?? unit.contents
  const items = Array.isArray(list) ? list : []
  // completionStatus → status map
  return items.map((o) => ({
    ...o,
    status:
      o.status ??
      (o.completionStatus != null ? COMPLETION_STATUS_MAP[o.completionStatus] ?? "not_started" : "not_started"),
    durationMinutes: o.durationMinutes ?? o.file?.durationMinutes,
  }))
}

/**
 * Video stream URL - GET /api/client/files/:fileId/stream
 * Auth gerektiği için blob URL döner (Authorization header ile fetch edilir)
 */
export async function getFileStreamUrl(fileId: string): Promise<string> {
  const response = await apiClient.get<Blob>(
    `${CLIENT_FILES_STREAM_ENDPOINT}/${fileId}/stream`,
    { responseType: "blob" }
  )
  return URL.createObjectURL(response.data)
}

/**
 * Dosya indirme linki (PDF vb.) — GET /api/client/files/:fileId/download
 * Backend imzalı link döner; link alanları `url` ya da `downloadUrl` olabilir.
 */
export async function getFileDownloadUrl(fileId: string): Promise<string> {
  const { data } = await apiClient.get<{
    success?: boolean
    data?: { url?: string; downloadUrl?: string }
    url?: string
    downloadUrl?: string
  }>(`${CLIENT_FILES_STREAM_ENDPOINT}/${fileId}/download`)
  const url =
    data?.data?.url ??
    data?.data?.downloadUrl ??
    data?.url ??
    data?.downloadUrl
  if (!url) throw new Error("Download URL not found")
  return url
}

/** POST /api/client/objectives/:objectiveId/video/watch – izleme ilerlemesi raporlama (her ~10–12 sn) */
export interface VideoWatchRequest {
  /** Son aralıkta oynatılan süre (delta, saniye). 0 = sayılmaz, >12 = seek kabul edilmez. */
  watchedSecondsDelta: number
  /** O anki video pozisyonu (saniye). */
  lastPositionSeconds: number
}

export interface VideoWatchResponse {
  watchedSeconds: number
  lastPositionSeconds: number
  progressPercent: number
  isCompleted: boolean
}

/**
 * Video hedefi izleme ilerlemesini raporlar. Her ~10 sn'de bir çağrılmalı.
 *
 * @example
 * // Eğitim video player içinde (EducationVideoPlayer):
 * setInterval(() => {
 *   reportObjectiveVideoWatch(objectiveId, {
 *     watchedSecondsDelta: playedSinceLastReport, // 0..12 kabul, >12 seek sayılmaz
 *     lastPositionSeconds: videoRef.current.currentTime,
 *   });
 * }, 10_000);
 */
export async function reportObjectiveVideoWatch(
  objectiveId: string,
  payload: VideoWatchRequest
): Promise<VideoWatchResponse> {
  const { data } = await apiClient.post<VideoWatchResponse>(
    `${CLIENT_OBJECTIVES_VIDEO_WATCH_ENDPOINT}/${objectiveId}/video/watch`,
    payload
  )
  return data
}

/**
 * GET /api/client/objectives/:objectiveId/video/watch — kullanıcının şu ana kadar
 * kaydedilmiş izleme ilerlemesini döner. Refresh / internet kesintisi sonrası
 * videoyu kaldığı yerden devam ettirmek için kullanılır.
 */
export async function getObjectiveVideoWatch(
  objectiveId: string
): Promise<VideoWatchResponse> {
  const { data } = await apiClient.get<VideoWatchResponse>(
    `${CLIENT_OBJECTIVES_VIDEO_WATCH_ENDPOINT}/${objectiveId}/video/watch`
  )
  return data
}

/**
 * Video olmayan hedefi tamamla – POST /api/client/education-enrollments/:enrollmentId/objectives/:objectiveId/complete
 * Body yok, Bearer JWT zorunlu. Tıklanır tıklanmaz (PDF/belge vb.) çağrılmalı.
 */
export async function completeObjective(
  enrollmentId: string,
  objectiveId: string
): Promise<void> {
  await apiClient.post(
    `${CLIENT_EDUCATION_ENROLLMENTS_ENDPOINT}/${enrollmentId}/objectives/${objectiveId}/complete`
  )
}

export const educationCatalogApi = {
  /**
   * Talep oluştur - POST /api/client/educations/:id/enrollments/request
   */
  requestEnrollment: async (educationId: string): Promise<void> => {
    await apiClient.post(
      `${CLIENT_EDUCATION_ENDPOINT}/${educationId}/enrollments/request`,
      {}
    )
  },

  /**
   * Eğitim detayı - GET /api/client/educations/:id
   */
  getEducationDetail: async (id: string): Promise<ClientEducationDetail> => {
    const response = await apiClient.get<EducationDetailApiResponse>(
      `${CLIENT_EDUCATION_ENDPOINT}/${id}`
    )
    const res = response.data
    // Backend: { success, status, data: BackendEducationDetail, message }
    const detail = (res?.data ?? res) as ClientEducationDetail
    return detail
  },

  /**
   * Eğitim kategorileri - GET /api/client/education-categories
   */
  getEducationCategories: async (): Promise<EducationCategory[]> => {
    const response = await apiClient.get(CLIENT_EDUCATION_CATEGORIES_ENDPOINT)
    const data = response.data?.data ?? response.data?.items ?? response.data
    const items = Array.isArray(data) ? data : []
    return items as EducationCategory[]
  },

  /**
   * Eğitim kategorileri (ağaç) - GET /api/client/education-categories/tree
   * Parent-child hiyerarşisi ile tüm kategorileri döner.
   */
  getEducationCategoriesTree: async (
    isActive?: boolean,
  ): Promise<EducationCategory[]> => {
    const response = await apiClient.get(
      `${CLIENT_EDUCATION_CATEGORIES_ENDPOINT}/tree`,
      { params: isActive !== undefined ? { isActive } : undefined },
    )
    const data = response.data?.data ?? response.data?.items ?? response.data
    const items = Array.isArray(data) ? data : []
    return items as EducationCategory[]
  },

  getInstructors: async (): Promise<InstructorListItem[]> => {
    const response = await apiClient.get(USERS_INSTRUCTORS_ENDPOINT)
    const data = response.data?.items ?? response.data?.data ?? response.data
    const items = Array.isArray(data) ? data : []
    return items as InstructorListItem[]
  },

  /**
   * GET /api/educations/:educationId/instructors
   * Eğitime atanmış eğitmenlerin alt kümesi. Sanal sınıf eğitmen havuzu artık
   * tüm domain eğitmenlerinden besleniyor; bu endpoint diff hesabı (bir
   * eğitmen eğitime önceden atanmış mı?) için kullanılıyor.
   */
  listEducationInstructors: async (
    educationId: string,
  ): Promise<{ userID: string; firstName: string | null; lastName: string | null }[]> => {
    const response = await apiClient.get(`/api/educations/${educationId}/instructors`)
    const data = response.data?.items ?? response.data?.data ?? response.data
    const items = Array.isArray(data) ? data : []
    return items as { userID: string; firstName: string | null; lastName: string | null }[]
  },

  /**
   * POST /api/educations/:educationId/instructors
   * Eğitime eğitmen atar. APPEND semantic — duplicate userID'ler atılmamalı,
   * çağıran taraf önce diff hesaplamalı.
   */
  addEducationInstructors: async (
    educationId: string,
    userIds: string[],
  ): Promise<void> => {
    if (userIds.length === 0) return
    if (userIds.length > 50) {
      throw new Error("Bir seferde en fazla 50 eğitmen atanabilir.")
    }
    await apiClient.post(`/api/educations/${educationId}/instructors`, { userIds })
  },

  /**
   * Client eğitim kataloğu - GET /api/client/educations/catalog
   * Backend pageSize ve sort parametrelerini kabul etmiyor
   */
  /**
   * Video olmayan hedefi tamamla – POST .../education-enrollments/:enrollmentId/objectives/:objectiveId/complete
   * Tıklanır tıklanmaz (PDF/belge vb.) çağrılır.
   */
  completeObjective,

  getCatalog: async (params?: {
    page?: number
    limit?: number
    search?: string
    type?: number
    categoryIds?: string[]
    instructorIds?: string[]
    sortBy?: string
    sortOrder?: string
  }) => {
    // Backend categoryIds[] formatını kabul etmiyor - virgülle ayrılmış string gönder
    const allowedParams: Record<string, string | number> = {}
    const sortBy = params?.sortBy ?? "requestedEnrollmentsCount"
    const sortOrder = params?.sortOrder ?? "desc"
    if (params?.page != null) allowedParams.page = params.page
    if (params?.limit != null) allowedParams.limit = params.limit
    if (params?.search) allowedParams.search = params.search
    if (params?.type != null) allowedParams.type = params.type
    if (params?.categoryIds?.length)
      allowedParams.categoryIds = params.categoryIds.join(",")
    if (params?.instructorIds?.length)
      allowedParams.instructorIds = params.instructorIds.join(",")
    if (sortBy) allowedParams.sortBy = sortBy
    if (sortOrder) allowedParams.sortOrder = sortOrder
    const response = await apiClient.get<ClientCatalogResponse>(
      CLIENT_CATALOG_ENDPOINT,
      { params: Object.keys(allowedParams).length > 0 ? allowedParams : undefined }
    )

    const resData = response.data ?? {}
    let items: unknown[] = Array.isArray(resData?.items)
      ? resData.items
      : Array.isArray(resData?.data)
        ? resData.data
        : []
    if (items.length === 0 && resData?.data && typeof resData.data === "object" && !Array.isArray(resData.data)) {
      const inner = resData.data as { items?: unknown[]; data?: unknown[] }
      items = Array.isArray(inner?.items) ? inner.items : Array.isArray(inner?.data) ? inner.data : []
    }
    const total = resData?.count ?? resData?.total ?? items.length

    return {
      data: (items as ClientEducationCatalogItem[]).map(mapToCatalogCard),
      total: typeof total === "number" ? total : items.length,
      page: params?.page ?? 1,
      pageSize: 10,
    }
  },

  /**
   * Sanal sınıf katılımcı listesi — her kayıtta firstName, lastName, title (nullable)
   * GET /api/client/classrooms/:classroomId/participants
   */
  getVirtualClassroomParticipants: async (
    classroomId: string,
  ): Promise<VirtualClassroomParticipant[]> => {
    const response = await apiClient.get<
      | VirtualClassroomParticipant[]
      | { data?: VirtualClassroomParticipant[]; participants?: VirtualClassroomParticipant[] }
    >(`${CLIENT_CLASSROOMS_ENDPOINT}/${classroomId}/participants`)
    const body = response.data
    if (Array.isArray(body)) return body
    const list = body?.data ?? body?.participants
    return Array.isArray(list) ? list : []
  },
}
